import { forwardRef, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";
import { twRing } from "@/lib/emotionTw";
import type { CardPadding, CardProps, CardVariant } from "./Card.types";

interface CardOwnerState {
  variant: CardVariant;
  padding: CardPadding;
  interactive: boolean;
}

/* Exact compiled output of the pre-migration Tailwind utilities plus the
 * shared `.bfrs-faded-card-border` component-layer rules (copied verbatim —
 * the global class keeps shipping until every dependent component is
 * migrated). Verified against the frozen baseline. */

const fadedCardBorder: CSSObject = {
  position: "relative",
  border: "1px solid transparent",
  isolation: "isolate",
  "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border)",
  "&:before": {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    padding: "1px",
    pointerEvents: "none",
    content: '""',
    background: "var(--bfrs-card-border-gradient)",
    borderRadius: "inherit",
    WebkitMask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    mask: "linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude"
  },
  "&:hover": {
    "--bfrs-card-border-gradient": "var(--bfrs-gradient-card-border-hover)"
  }
};

const cardBase: CSSObject = {
  borderRadius: "var(--bfrs-radius-xl)",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s"
};

const cardVariantStyles: Record<CardVariant, CSSObject> = {
  default: {
    ...fadedCardBorder,
    backgroundImage: "var(--bfrs-gradient-card)",
    "--tw-shadow": "var(--bfrs-shadow-card)",
    "--tw-shadow-colored": "var(--bfrs-shadow-card)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  elevated: {
    ...fadedCardBorder,
    backgroundImage: "var(--bfrs-gradient-card)",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  bordered: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "0 0 #0000",
    "--tw-shadow-colored": "0 0 #0000",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  muted: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
    "--tw-bg-opacity": "1",
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / var(--tw-bg-opacity, 1))",
    "--tw-shadow": "0 0 #0000",
    "--tw-shadow-colored": "0 0 #0000",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  },
  soft: {
    borderWidth: "1px",
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary-soft) / var(--tw-border-opacity, 1))",
    backgroundColor: "hsl(var(--bfrs-color-primary-soft) / .6)",
    "--tw-shadow": "0 0 #0000",
    "--tw-shadow-colored": "0 0 #0000",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  }
};

const cardPaddingStyles: Record<CardPadding, CSSObject> = {
  none: { padding: 0 },
  sm: { padding: ".75rem" },
  md: { padding: "1rem" },
  lg: { padding: "1.25rem" }
};

const cardInteractiveStyle: CSSObject = {
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "hsl(var(--bfrs-color-surface-muted) / .7)"
  },
  "&:focus-within": twRing("2px", "hsl(var(--bfrs-color-border-focus) / .2)")
};

const CardRoot = styled("div", {
  label: "AgenticCard-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: CardOwnerState }>(({ ownerState }) => [
  cardBase,
  cardVariantStyles[ownerState.variant],
  cardPaddingStyles[ownerState.padding],
  ownerState.interactive && cardInteractiveStyle,
  ownerState.variant === "bordered" &&
    ownerState.padding === "md" && {
      paddingLeft: "22px",
      paddingRight: "22px",
      paddingTop: "18px",
      paddingBottom: "18px"
    }
]);

export const Card = forwardRef<HTMLDivElement, CardProps>(({ className, variant, padding, interactive, ...props }, ref) => (
  <CardRoot
    ref={ref}
    ownerState={{ variant: variant ?? "default", padding: padding ?? "md", interactive: Boolean(interactive) }}
    className={className}
    {...props}
  />
));

Card.displayName = "Card";

const CardHeaderRoot = styled("div", {
  label: "AgenticCard-header"
})({
  borderBottomWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

export const CardHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <CardHeaderRoot ref={ref} className={className} {...props} />
));

CardHeader.displayName = "CardHeader";

const CardContentRoot = styled("div", {
  label: "AgenticCard-content"
})({
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: "1rem",
  paddingBottom: "1rem"
});

export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <CardContentRoot ref={ref} className={className} {...props} />
));

CardContent.displayName = "CardContent";

const CardFooterRoot = styled("div", {
  label: "AgenticCard-footer"
})({
  borderTopWidth: "1px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-border) / var(--tw-border-opacity, 1))",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  paddingTop: ".75rem",
  paddingBottom: ".75rem"
});

export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <CardFooterRoot ref={ref} className={className} {...props} />
));

CardFooter.displayName = "CardFooter";
